/**
 * Look mode — a persistent cursor stance behind `x` + direction.
 *
 * Pure, and separate from the game loop, because the interesting parts are
 * rules rather than plumbing: where the cursor may go, and what counts as a
 * new tile worth asking the server about.
 *
 * Two things it deliberately does *not* do:
 *
 * - **It does not describe anything.** Descriptions are composed in
 *   `@annwn/sim` beside the content that defines them (M0-06), so the client
 *   never holds a table of what a tile reads like — which is what lets M3-01's
 *   worldgen add terrain without touching this app at all.
 * - **It does not decide what is visible.** It clamps to the map and to the
 *   server's own examine range, and the server re-checks both. A client-side
 *   bound is a convenience for the player, never a permission.
 */

import { DAY_SIGHT, DIRECTION_VECTORS, withinEarshot, type Direction } from '@annwn/protocol';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Bounds {
  readonly width: number;
  readonly height: number;
}

/**
 * The optimistic default before the first sky snapshot. Live callers pass the
 * viewer's current `sky.sight`, so fog, darkness, firelight and injured eyes
 * all constrain Look by the same rule that constrains visible creatures.
 */
export const LOOK_RANGE = DAY_SIGHT;

export class Look {
  private cursor: Point | null = null;

  /** Where the cursor is, or `null` when look mode is closed. */
  get at(): Point | null {
    return this.cursor;
  }

  get active(): boolean {
    return this.cursor !== null;
  }

  /**
   * Open on the player's own tile. Opening does not itself examine anything:
   * the mode stays on while clicks choose which tiles deserve a window.
   */
  open(origin: Point): Point {
    this.cursor = { x: origin.x, y: origin.y };
    return this.cursor;
  }

  close(): void {
    this.cursor = null;
  }

  /**
   * Step the cursor, or return `null` if the step is refused — off the map, or
   * further than the server would answer for.
   *
   * Refusing rather than clamping is the honest behaviour: a cursor that slides
   * along an invisible wall tells the player their key did something, and it
   * did not.
   */
  step(
    direction: Direction,
    origin: Point,
    bounds: Bounds,
    range: number = LOOK_RANGE,
  ): Point | null {
    if (!this.cursor) return null;

    const [dx, dy] = DIRECTION_VECTORS[direction];
    const next = { x: this.cursor.x + dx, y: this.cursor.y + dy };

    if (!inBounds(next, bounds)) return null;
    if (!withinEarshot(origin, next.x, next.y, range)) return null;

    this.cursor = next;
    return next;
  }

  /** Point the cursor somewhere directly — what a mouse does. */
  moveTo(target: Point, origin: Point, bounds: Bounds, range: number = LOOK_RANGE): Point | null {
    if (!this.cursor) return null;
    if (!inBounds(target, bounds)) return null;
    if (!withinEarshot(origin, target.x, target.y, range)) return null;

    this.cursor = { x: target.x, y: target.y };
    return this.cursor;
  }

  /**
   * Keep the cursor in reach as the player walks.
   *
   * Without this, walking away from a cursor leaves it stranded outside the
   * range the server will answer for, and every subsequent keystroke is
   * refused for a reason the player cannot see. Closing is the least surprising
   * repair: the cursor was about something you have walked away from.
   */
  reanchor(origin: Point, range: number = LOOK_RANGE): void {
    if (!this.cursor) return;
    if (withinEarshot(origin, this.cursor.x, this.cursor.y, range)) return;
    this.cursor = { x: origin.x, y: origin.y };
  }
}

function inBounds(point: Point, bounds: Bounds): boolean {
  return point.x >= 0 && point.y >= 0 && point.x < bounds.width && point.y < bounds.height;
}
